import { SupplyItemStatus, SPECIMEN_BAG_SUPPLY_ID } from '@caresend/types';
import { hasStatus, getSupplyItemPath } from '@caresend/utils';
import 'firebase/auth';
import '../../../database/firebaseConfig/cloudFunctions.js';
import '../../../database/firebaseConfig/init.js';
import { getValueOnce } from '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import { getRouter } from '../../../router/helpers/initRouter.js';
import { getStore } from '../../helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import 'immutability-helper';
import '../../../router/routes/dev/model.js';
import '../../../router/routes/dev/routes.js';
import { homeRoute } from '../../../router/routes/locations.js';
import '../../../router/routes/orderFlow/model.js';
import '../../../router/routes/orderFlow/privateHelpers.js';
import '../../../router/routes/orderFlow/routes.js';
import '../../../router/routes/schedulingFlow/model.js';
import '../../../router/routes/schedulingFlow/routes.js';
import '../../../router/routes/chartingFlow/model.js';
import '../../../router/routes/chartingFlow/routes.js';
import '../../../router/routes/portal/portalRoutes.js';
import '../../../router/routes/portal/model.js';
import '../../../router/routes/pro/model.js';
import '../../../router/routes/visitsPage/model.js';
import '../../../router/routes/visitsPage/routes.js';

const getChartingFlowState = () => getStore().state.chartingFlow;
/** Do not call before IDs have been fixed with `fixSpecimenBagIDs`. */
const getLocalSpecimenBags = (orderID) => {
    var _a, _b;
    const state = getChartingFlowState();
    return Object.values((_b = (_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.specimenBags) !== null && _b !== void 0 ? _b : {});
};
/** Currently SKU ID will always be SPECIMEN_BAG_2 */
const getActualSpecimenBagID = ({ skuID, serial }) => {
    if (serial === '')
        throw Error('Missing serial on specimen bag');
    return `${skuID}-${serial}`;
};
const getSpecimenBagSupplyItem = async (localSpecimenBag) => {
    const specimenBagID = getActualSpecimenBagID(localSpecimenBag);
    const specimenBagSupplyItem = await getValueOnce(getSupplyItemPath(specimenBagID));
    if (!specimenBagSupplyItem) {
        throw Error(`Specimen bag with serial ${localSpecimenBag.serial} was not found.`
            + ' Please ensure that it is correct, or contact CareSend support'
            + ' if you continue to encounter issues.');
    }
    return specimenBagSupplyItem;
};
const aSerialIsUsedMoreThanOnce = (orderID) => {
    var _a, _b, _c;
    const state = getChartingFlowState();
    const serials = Object.values((_c = (_b = (_a = state.data) === null || _a === void 0 ? void 0 : _a[orderID]) === null || _b === void 0 ? void 0 : _b.specimenBags) !== null && _c !== void 0 ? _c : {});
    const serialsArray = serials.map((specimenBag) => specimenBag.serial);
    const serialsSet = new Set(serialsArray);
    /**
     * Sets don't allow duplicate entries, so if the lengths of the Set and the
     * Array are different, then there must be duplicates
     */
    return serialsSet.size !== serialsArray.length;
};
/**
 * Check that unused supply items exist matching every specimen bag ID, throw
 * errors otherwise. Do not call before IDs have been fixed with
 * `fixSpecimenBagIDs`.
 */
const validateSpecimenBagIDs = async (localSpecimenBags) => {
    const promises = localSpecimenBags.map(getSpecimenBagSupplyItem);
    const supplyItems = await Promise.all(promises);
    const firstUsedSupplyItem = supplyItems.find((supplyItem) => hasStatus(supplyItem.status, SupplyItemStatus.USED));
    if (firstUsedSupplyItem) {
        throw Error(`The specimen bag with serial ${firstUsedSupplyItem.serial} has already`
            + ' been marked as used');
    }
};
/**
 * `LocalSpecimenBag` is used locally to track specimen bags in the charting
 * flow. Specimen bags are saved as `SupplyItemWithSupplyID`s in the DB.
 */
const getSpecimenBag = (localSpecimenBag) => ({
    id: localSpecimenBag.id,
    supplyID: SPECIMEN_BAG_SUPPLY_ID,
});
/**
 * Specimen bag IDs that have not yet been saved to the DB are random IDs.
 * Replace all IDs based on the entered/scanned serial.
 *
 * Example:
 * ```
 * {
 *   '3e5euxon25qkvlg9z12': {
 *     id: '3e5euxon25qkvlg9z12',
 *     serial: '100002086',
 *     ...
 *   },
 *   ...
 * }
 * ⬇️
 * {
 *   'SPECIMEN_BAG_2-100002086': {
 *     id: 'SPECIMEN_BAG_2-100002086',
 *     serial: '100002086',
 *     ...
 *   },
 *   ...
 * }
 * ```
 */
const fixSpecimenBagIDs = (orderID) => {
    const { commit } = getStore();
    const specimenBags = getLocalSpecimenBags(orderID);
    specimenBags.forEach((specimenBag) => {
        commit('chartingFlow/REPLACE_ID_ON_SPECIMEN_BAG', {
            orderID,
            currentSpecimenBagID: specimenBag.id,
            actualSpecimenBagID: getActualSpecimenBagID(specimenBag),
        });
    });
};
/**
 * Gets specimen bags for storage on procedures.
 */
const getDataForFullOrder = (localSpecimenBags) => {
    const specimenBagSupplyItemsByProcedure = localSpecimenBags.reduce((items, localSpecimenBag) => {
        const { procedureID, serial } = localSpecimenBag;
        if (!procedureID) {
            throw Error(`Missing procedure on specimen bag ${serial}`);
        }
        const specimenBag = getSpecimenBag(localSpecimenBag);
        return {
            ...items,
            [procedureID]: specimenBag,
        };
    }, {});
    return { specimenBagSupplyItemsByProcedure };
};
const setSpecimenBagsOnDraftBooking = (localSpecimenBags, draftBookingID, orderID) => {
    const { commit } = getStore();
    const specimenBagsByTaskID = localSpecimenBags.reduce((bags, localSpecimenBag) => {
        const { serial, taskID } = localSpecimenBag;
        if (!taskID)
            throw Error(`Missing task on specimen bag ${serial}`);
        const specimenBag = getSpecimenBag(localSpecimenBag);
        return {
            ...bags,
            [taskID]: specimenBag,
        };
    }, {});
    commit('chartingFlow/SET_SPECIMEN_BAGS_ON_DRAFT_BOOKING', {
        orderID,
        draftBookingID,
        specimenBagsByTaskID,
    });
};
/**
 * For draft orders, all specimen bags entered are stored on the first booking
 * found on the order. We do not support multiple bookings on in-office orders.
 * When the draft order is converted to a full order, the specimen bags will
 * be stored on the appropriate procedures based on their associated task.
 *
 * Returns:
 * - Draft bookings with specimen bags stored on first.
 */
const getDataForDraftOrder = (localSpecimenBags, orderID) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { state: rootState } = getStore();
    const state = getChartingFlowState();
    const officeID = (_b = (_a = rootState.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.id;
    if (!officeID)
        throw Error('Missing office id');
    const draftOrder = (_c = state.data[orderID]) === null || _c === void 0 ? void 0 : _c.order;
    if (!draftOrder)
        throw Error(`Missing draft order ${orderID}`);
    const firstBookingID = (_f = Object.values((_e = (_d = state.data[orderID]) === null || _d === void 0 ? void 0 : _d.bookings) !== null && _e !== void 0 ? _e : {})[0]) === null || _f === void 0 ? void 0 : _f.id;
    if (!firstBookingID)
        throw Error('There is no draft booking on the draft order');
    // Overwrite specimen bags on first (only) draft booking in the store
    setSpecimenBagsOnDraftBooking(localSpecimenBags, firstBookingID, orderID);
    // Get updated draft bookings and procedures from the store
    const draftBookings = (_g = state.data[orderID]) === null || _g === void 0 ? void 0 : _g.bookings;
    if (!draftBookings)
        throw Error('Missing draft bookings');
    /**
     * @deprecated
     *
     * No changes are currently made to procedures on a draft order, but the cloud
     * function requires this arg. Previously the draft procedures' statuses were
     * set to `completed`. This is no longer desired. Instead the status on the
     * procedures should remain `created` until the user completes the charting
     * flow on the full order to ensure `Procedure.specimenBag` gets set.
     */
    const draftProcedures = (_h = state.data[orderID]) === null || _h === void 0 ? void 0 : _h.procedures;
    if (!draftProcedures)
        throw Error('Missing draft procedures');
    return {
        draftBookings,
        draftProcedures,
    };
};
/**
 * Returns args for `completeInOfficeChartingRequest` cloud function. If the
 * data is not valid to save the charting flow, throws an error.
 */
const getValidatedCompleteInOfficeChartingRequest = async (orderID) => {
    const { getters } = getStore();
    const isDraft = getters['chartingFlow/isDraftOrder'](orderID);
    const request = {
        orderID,
        isDraft,
    };
    if (aSerialIsUsedMoreThanOnce(orderID)) {
        throw Error('There is a serial that is used more than once. Please ensure that each'
            + ' serial is unique.');
    }
    fixSpecimenBagIDs(orderID);
    const localSpecimenBags = getLocalSpecimenBags(orderID);
    validateSpecimenBagIDs(localSpecimenBags);
    if (isDraft) {
        request.draftData = getDataForDraftOrder(localSpecimenBags, orderID);
    }
    else {
        request.fullData = getDataForFullOrder(localSpecimenBags);
    }
    return request;
};
const navToExitRoute = (id, isDraft) => {
    var _a;
    const { getters } = getStore();
    const router = getRouter();
    const route = (_a = getters['chartingFlow/getExitRoute']) === null || _a === void 0 ? void 0 : _a.call(getters, { id, isDraft });
    router.push(route !== null && route !== void 0 ? route : homeRoute());
};

export { aSerialIsUsedMoreThanOnce, getValidatedCompleteInOfficeChartingRequest, navToExitRoute };
